import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { colorFor, initials } from "../utils/format";
import { awaitingReview } from "../utils/jobs";
import { LogoMark } from "./Logo";
import PlatypusLogo from "./PlatypusLogo";

export function NavIcon({ item }) {
  if (item.platypus) return <PlatypusLogo size={22} className="nav-platypus" />;
  return <i className={`bi ${item.icon}`} />;
}

export const NAV_MAIN = [
  { to: "/", label: "Home", icon: "bi-house", end: true },
  { to: "/find", label: "Find jobs", icon: "bi-search" },
  { to: "/platypus", label: "Platypus", platypus: true },
  { to: "/tracker", label: "Tracker", icon: "bi-kanban" },
  { to: "/contacts", label: "Contacts", icon: "bi-people" },
];

export const NAV_SETUP = [
  { to: "/documents", label: "Documents", icon: "bi-folder2" },
  { to: "/profile", label: "Profile", icon: "bi-person-circle" },
  { to: "/memory", label: "Memory", icon: "bi-lightbulb" },
];

const NAV_ADMIN = { to: "/admin", label: "Admin", icon: "bi-shield-lock" };
const NAV_SUPERADMIN = { to: "/superadmin", label: "Super Admin", icon: "bi-shield-check" };

function useSetupNav() {
  const { user } = useAuth();
  if (user?.role === "superadmin") return [{ to: "/profile", label: "Profile", icon: "bi-person-circle" }];
  return user?.role === "admin" ? [...NAV_SETUP, NAV_ADMIN] : NAV_SETUP;
}

function useMainNav() {
  const { user } = useAuth();
  if (user?.role === "superadmin") return [NAV_SUPERADMIN];
  return NAV_MAIN;
}

function Brand() {
  const { user } = useAuth();
  const home = user?.role === "superadmin" ? "/superadmin" : "/";
  return (
    <Link to={home} className="brand">
      <LogoMark />
      <span className="brand-text">Plateo</span>
    </Link>
  );
}

function SideLink({ item, badge }) {
  return (
    <NavLink to={item.to} end={item.end} className="side-link">
      <NavIcon item={item} />
      <span className="flex-grow-1">{item.label}</span>
      {badge > 0 && <span className="side-badge">{badge}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const { jobs, openAddJob, integrations } = useApp();
  const { user } = useAuth();
  const setupNav = useSetupNav();
  const mainNav = useMainNav();
  const opsOnly = user?.role === "superadmin";
  const badges = {
    "/tracker": jobs.filter((j) => j.stage !== "rejected").length,
    "/platypus": jobs.filter(awaitingReview).length,
    "/admin": integrations.admin?.pending,
  };

  return (
    <aside className="sidebar d-none d-lg-flex">
      <Brand />

      {opsOnly ? (
        <div className="my-3" aria-hidden="true" />
      ) : (
        <button
          className="btn btn-primary w-100 my-3"
          onClick={() => openAddJob()}
        >
          <i className="bi bi-plus-lg me-1" /> Add job
        </button>
      )}

      <nav className="d-flex flex-column gap-1">
        {mainNav.map((item) => (
          <SideLink
            key={item.to}
            item={item}
            badge={badges[item.to] || 0}
          />
        ))}
      </nav>

      <div className="side-section-label">{opsOnly ? "Account" : "Setup"}</div>
      <nav className="d-flex flex-column gap-1">
        {setupNav.map((item) => (
          <SideLink key={item.to} item={item} badge={badges[item.to] || 0} />
        ))}
      </nav>

      <div style={{ marginTop: "auto" }}>
        <UserCard />
        <div className="side-legal">
          <Link to="/welcome">About</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </div>
      </div>
    </aside>
  );
}

function UserCard() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="user-card">
      <span
        className="avatar avatar-sm"
        style={{ background: colorFor(user.name) }}
      >
        {initials(user.name)}
      </span>
      <div className="min-w-0 flex-grow-1">
        <div className="small fw-medium text-truncate">{user.name}</div>
        <div className="text-muted text-truncate" style={{ fontSize: 12 }}>
          {user.email}
        </div>
      </div>
      <button
        className="btn btn-sm btn-light"
        onClick={logout}
        title="Log out"
        aria-label="Log out"
      >
        <i className="bi bi-box-arrow-right" />
      </button>
    </div>
  );
}

export function MobileTopBar() {
  return (
    <header className="mobile-topbar d-lg-none">
      <Brand />
      <NavLink
        to="/profile"
        className="btn btn-light btn-sm rounded-circle"
        aria-label="Profile"
      >
        <i className="bi bi-person" />
      </NavLink>
    </header>
  );
}

export function MobileBottomNav() {
  const { openAddJob } = useApp();
  const { logout, user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const setupNav = useSetupNav();
  const opsOnly = user?.role === "superadmin";
  const moreItems = opsOnly
    ? setupNav
    : [...NAV_MAIN.filter((item) => ["/platypus", "/contacts"].includes(item.to)), ...setupNav];

  if (opsOnly) {
    return (
      <nav className="bottom-nav d-lg-none">
        <NavLink to="/superadmin" className="bottom-link">
          <i className="bi bi-shield-check" />
          <span>Ops</span>
        </NavLink>
        <NavLink to="/profile" className="bottom-link">
          <i className="bi bi-person" />
          <span>Profile</span>
        </NavLink>
        <button className="bottom-link" onClick={logout}>
          <i className="bi bi-box-arrow-right" />
          <span>Log out</span>
        </button>
      </nav>
    );
  }

  return (
    <>
      {moreOpen && (
        <>
          <div className="sheet-backdrop" onClick={() => setMoreOpen(false)} />
          <div className="bottom-sheet">
            {moreItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="side-link"
                onClick={() => setMoreOpen(false)}
              >
                <NavIcon item={item} />
                {item.label}
              </NavLink>
            ))}
            <Link to="/welcome" className="side-link" onClick={() => setMoreOpen(false)}>
              <i className="bi bi-info-circle" />
              About Plateo
            </Link>
            <button
              className="side-link w-100 border-0 bg-transparent"
              onClick={logout}
            >
              <i className="bi bi-box-arrow-right" />
              Log out
            </button>
          </div>
        </>
      )}
      <nav className="bottom-nav d-lg-none">
        {NAV_MAIN.slice(0, 2).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className="bottom-link"
            onClick={() => setMoreOpen(false)}
          >
            <i className={`bi ${item.icon}`} />
            <span>{item.label.split(" ")[0]}</span>
          </NavLink>
        ))}
        <button
          className="bottom-add"
          onClick={() => {
            setMoreOpen(false);
            openAddJob();
          }}
          aria-label="Add job"
        >
          <i className="bi bi-plus-lg" />
        </button>
        <NavLink
          to="/tracker"
          className="bottom-link"
          onClick={() => setMoreOpen(false)}
        >
          <i className="bi bi-kanban" />
          <span>Tracker</span>
        </NavLink>
        <button
          className={`bottom-link ${moreOpen ? "active" : ""}`}
          onClick={() => setMoreOpen((o) => !o)}
        >
          <i className="bi bi-three-dots" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
